import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  IonButton,
  IonCard,
  IonContent,
  IonDatetime,
  IonDatetimeButton,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  useIonToast,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from "@ionic/react";
import useUserGroupsByIdAndManager from "../../../hooks/groups/useUserGroupsByIdAndManager";
import { AuthenticationContext } from "../../../providers/AuthenticationProvider/AuthenticationProvider";
import useBookingsByClubAndFields from "../../../hooks/bookings/useBookingsByClubAndFields";
import Spinner from "../../../components/Spinner/Spinner";
import SchedulerCard from "../../../components/SchedulerCard/SchedulerCard";
import useAPI from "../../../hooks/basic/useAPI";
import { doPlanification } from "./doPlanification";
import { toLocalISO } from "../../../utils/datetime";

// Default planning window: today → one month ahead. Computed at call time (not at
// module load) so it never goes stale if the app stays open across days.
const defaultFrom = () => toLocalISO(new Date());
const defaultTo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return toLocalISO(d);
};

function GroupScheduler({ match, history }) {
  const parsedGroupId = match?.params?.groupId;
  const { appUserId } = useContext(AuthenticationContext);
  const { callAPI } = useAPI();
  const [present] = useIonToast();
  const { data, isLoading: isLoadingGroup } = useUserGroupsByIdAndManager(
    parsedGroupId,
    appUserId,
    !!parsedGroupId,
  );

  // A deleted (or non-existent) group must not be plannable via its URL: once
  // the fetch settles to null, bounce back to the groups list.
  useEffect(() => {
    if (parsedGroupId && !isLoadingGroup && data === null) {
      present({
        message: "Gruppo non più disponibile",
        duration: 3000,
        color: "warning",
        position: "bottom",
      });
      history?.replace("/admin/groups");
    }
  }, [parsedGroupId, isLoadingGroup, data]);
  const [isSaving, setIsSaving] = useState(false);
  const [schedulerPreview, setSchedulerPreview] = useState([]);
  const [scheduleFromDate, setScheduleFromDate] = useState(defaultFrom);
  // toDate is fromDate + 1 month
  const [scheduleToDate, setScheduleToDate] = useState(defaultTo);
  const { field } = data || {};

  const clubId = field?.split("/")[1];
  const fieldId = field?.split("/")[3];

  const {
    data: savedUserEvents,
    refreshData: refreshBookingData,
    isLoading,
  } = useBookingsByClubAndFields(
    clubId,
    fieldId,
    new Date(scheduleFromDate),
    new Date(scheduleToDate),
    !data,
  );

  const initState = () => {
    setScheduleFromDate(defaultFrom());
    setScheduleToDate(defaultTo());
    refreshBookingData();
    setSchedulerPreview([]);
  };

  useIonViewWillEnter(() => {
    initState();
  });

  useIonViewDidLeave(() => {
    initState();
  });

  const onPreviewClick = () => {
    setSchedulerPreview([]);
    refreshBookingData();
    const {
      participants,
      club,
      clubName,
      fieldName,
      field,
      fromTime,
      toTime,
      dayOfWeek,
      maxParticipants,
      tags,
      label,
      description,
    } = data;
    const scheduler = doPlanification(
      appUserId,
      new Date(scheduleFromDate),
      new Date(scheduleToDate),
      fromTime.seconds * 1000,
      toTime.seconds * 1000,
      Number(dayOfWeek),
      participants,
      club,
      field,
      parsedGroupId,
      maxParticipants,
      tags,
      label,
      description,
      clubName,
      fieldName,
    );

    // for each booking in scheduler, check if there is an overlap with savedUserEvents
    // if there is an overlap, add a overlapped property to the booking
    scheduler.forEach((booking) => {
      const overlaps = (savedUserEvents || []).filter((event) => {
        const from = new Date(event.from.seconds * 1000);
        const to = new Date(event.to.seconds * 1000);
        return (
          (booking.from >= from && booking.from < to) ||
          (booking.to > from && booking.to <= to) ||
          (booking.from <= from && booking.to >= to)
        );
      });
      const relatedBooking = overlaps.find(
        (overlap) =>
          overlap.from.seconds * 1000 === booking.from.getTime() &&
          overlap.to.seconds * 1000 === booking.to.getTime() &&
          overlap.relatedGroupId === booking.relatedGroupId,
      );
      if (overlaps.length > 0) {
        booking.overlapped = true;
        booking.overlaps = overlaps;
        booking.relatedBooking = relatedBooking;
        // if from and to are the same, it means that the booking is already present
        // in the database
      }
    });

    setSchedulerPreview(scheduler);
  };

  const onSaveClick = async () => {
    if (isSaving) return;
    // remove overlapped and relatedBooking events from schedulerPreview
    const bookingsToSave = schedulerPreview.filter(
      (booking) => !booking.overlapped && !booking.relatedBooking,
    );

    if (bookingsToSave.length === 0) {
      present({
        message: "Nessuna lezione da salvare",
        duration: 3000,
        color: "warning",
        position: "bottom",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Single idempotent server call: creates the bookings together with each
      // participant's monthly group-log + lesson-log. No client-side two-step
      // (no orphans), no duplicates when the same plan is saved again.
      await callAPI({
        method: "post",
        url: "/plan-group-lessons",
        data: { bookings: bookingsToSave },
      });
      setSchedulerPreview([]);
      refreshBookingData();
      present({
        message: `${bookingsToSave.length} lezioni pianificate con successo`,
        duration: 3000,
        color: "success",
        position: "bottom",
      });
    } catch (error) {
      console.error("Errore nel salvataggio della pianificazione", error);
      present({
        message: "Errore nel salvataggio della pianificazione",
        duration: 4000,
        color: "danger",
        position: "bottom",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <IonPage>
      <IonContent>
        {(isLoading || isSaving) && <Spinner />}
        <IonTitle
          style={{
            color: "#062a40",
          }}
        >
          <h1>Pianificazione {data?.description}</h1>
        </IonTitle>
        <form>
          <IonList>
            <IonItem>
              <IonLabel>Data Inizio</IonLabel>
              <IonDatetimeButton datetime="schedulerFromDate"></IonDatetimeButton>
              <IonModal keepContentsMounted={true}>
                <IonDatetime
                  presentation="date"
                  id="schedulerFromDate"
                  value={scheduleFromDate}
                  onIonChange={(e) => {
                    setScheduleFromDate(e.detail.value);
                    setSchedulerPreview([]);
                  }}
                ></IonDatetime>
              </IonModal>
            </IonItem>
            <IonItem>
              <IonLabel>Data Fine</IonLabel>
              <IonDatetimeButton datetime="schedulerToDate"></IonDatetimeButton>
              <IonModal keepContentsMounted={true}>
                <IonDatetime
                  presentation="date"
                  id="schedulerToDate"
                  value={scheduleToDate}
                  onIonChange={(e) => {
                    setScheduleToDate(e.detail.value);
                    setSchedulerPreview([]);
                  }}
                ></IonDatetime>
              </IonModal>
            </IonItem>
          </IonList>
        </form>
        {/* <p>{JSON.stringify(schedulerPreview)}</p> */}
        <IonButton onClick={onPreviewClick}>Anteprima</IonButton>
        {schedulerPreview && (
          <div>
            {schedulerPreview
              // .sort((a, b) => a.from.seconds - b.from.seconds)
              .map((booking) => (
                <SchedulerCard
                  key={`${booking.relatedGroupId}-${booking.from.getTime()}`}
                  data={{
                    ...booking,
                    from: { seconds: booking.from.getTime() / 1000 },
                    to: { seconds: booking.to.getTime() / 1000 },
                  }}
                  imageUrl="smash.png"
                />
              ))}
          </div>
        )}
        <IonButton onClick={onSaveClick} disabled={isSaving}>
          Salva
        </IonButton>
      </IonContent>
    </IonPage>
  );
}

GroupScheduler.propTypes = {};

export default GroupScheduler;

GroupScheduler.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      groupId: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
};
